// components/elements/Lightbox/Lightbox.jsx
import { useState, useEffect } from 'react';
import './Lightbox.css';

const Lightbox = ({ children }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [zoomLevel, setZoomLevel] = useState(1);
    const [isDragging, setIsDragging] = useState(false);
    const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
    const [imagePosition, setImagePosition] = useState({ x: 0, y: 0 });
    
    const items = [];
    const childrenArray = [];

    const extractImages = (element, index) => {
        if (!element) return;
        
        if (element.type === 'img') {
            items.push({
                src: element.props.src,
                alt: element.props.alt || 'Zdjęcie',
                element: element
            });
            return;
        }
        
        if (element.props && element.props.children) {
            if (Array.isArray(element.props.children)) {
                element.props.children.forEach(child => {
                    extractImages(child);
                });
            } else {
                extractImages(element.props.children);
            }
        }
    };

    children.forEach((child) => {
        if (child) {
            extractImages(child);
        }
    });

    let imageIndex = 0;
    const processChildren = (element) => {
        if (!element) return null;
        
        if (element.type === 'img') {
            const currentImageIndex = imageIndex;
            imageIndex++;
            return (
                <div 
                    key={`img-wrapper-${currentImageIndex}`}
                    onClick={() => openLightbox(currentImageIndex)}
                    style={{ cursor: 'pointer', display: 'inline-block' }}
                    className="lightbox-trigger-wrapper"
                >
                    {element}
                </div>
            );
        }
        
        if (element.props && element.props.children) {
            const children = Array.isArray(element.props.children) 
                ? element.props.children 
                : [element.props.children];
            
            const processedChildren = children.map(child => processChildren(child));
            
            return {
                ...element,
                props: {
                    ...element.props,
                    children: processedChildren.length === 1 
                        ? processedChildren[0] 
                        : processedChildren
                }
            };
        }
        
        return element;
    };

    children.forEach((child) => {
        if (child) {
            const processed = processChildren(child);
            if (processed) {
                childrenArray.push(processed);
            }
        }
    });

    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
            setZoomLevel(1);
            setImagePosition({ x: 0, y: 0 });
        }
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [isOpen]);

    useEffect(() => {
        const handleKeyDown = (e) => {
            if (!isOpen) return;
            if (e.key === 'Escape') closeLightbox();
            if (e.key === 'ArrowLeft') prevImage();
            if (e.key === 'ArrowRight') nextImage();
            if (e.key === '=' || e.key === '+') zoomIn(e);
            if (e.key === '-') zoomOut(e);
            if (e.key === '0') resetZoom(e);
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isOpen]);

    useEffect(() => {
        const handleWheel = (e) => {
            if (!isOpen) return;
            e.preventDefault();
            if (e.deltaY < 0) {
                zoomIn(e);
            } else {
                zoomOut(e);
            }
        };

        document.addEventListener('wheel', handleWheel, { passive: false });
        return () => document.removeEventListener('wheel', handleWheel);
    }, [isOpen]);

    const openLightbox = (index) => {
        if (items.length === 0) return;
        setCurrentIndex(index);
        setIsOpen(true);
        setZoomLevel(1);
        setImagePosition({ x: 0, y: 0 });
    };

    const closeLightbox = () => {
        setIsOpen(false);
        setZoomLevel(1);
        setImagePosition({ x: 0, y: 0 });
    };

    const prevImage = (e) => {
        e?.stopPropagation();
        setCurrentIndex((prev) => 
            prev > 0 ? prev - 1 : items.length - 1
        );
        setZoomLevel(1);
        setImagePosition({ x: 0, y: 0 });
    };

    const nextImage = (e) => {
        e?.stopPropagation();
        setCurrentIndex((prev) => 
            prev < items.length - 1 ? prev + 1 : 0
        );
        setZoomLevel(1);
        setImagePosition({ x: 0, y: 0 });
    };

    const zoomIn = (e) => {
        e?.stopPropagation();
        setZoomLevel((prev) => Math.min(prev + 0.25, 5));
    };

    const zoomOut = (e) => {
        e?.stopPropagation();
        setZoomLevel((prev) => {
            const newZoom = Math.max(prev - 0.25, 0.5);
            if (newZoom === 0.5) {
                setImagePosition({ x: 0, y: 0 });
            }
            return newZoom;
        });
    };

    const resetZoom = (e) => {
        e?.stopPropagation();
        setZoomLevel(1);
        setImagePosition({ x: 0, y: 0 });
    };

    const handleMouseDown = (e) => {
        if (zoomLevel > 1) {
            setIsDragging(true);
            setDragStart({
                x: e.clientX - imagePosition.x,
                y: e.clientY - imagePosition.y
            });
        }
    };

    const handleMouseMove = (e) => {
        if (isDragging && zoomLevel > 1) {
            setImagePosition({
                x: e.clientX - dragStart.x,
                y: e.clientY - dragStart.y
            });
        }
    };

    const handleMouseUp = () => {
        setIsDragging(false);
    };

    const currentItem = items[currentIndex];

    return (
        <>
            {childrenArray}

            {isOpen && items.length > 0 && currentItem && (
                <div className="lightbox-overlay" onClick={closeLightbox}>
                    <button 
                        className="lightbox-close" 
                        onClick={closeLightbox}
                        aria-label="Zamknij"
                    >
                        ✕
                    </button>

                    <button 
                        className="lightbox-prev" 
                        onClick={prevImage}
                        aria-label="Poprzednie"
                    >
                        ‹
                    </button>

                    <div 
                        className="lightbox-content" 
                        onClick={(e) => e.stopPropagation()}
                        onMouseDown={handleMouseDown}
                        onMouseMove={handleMouseMove}
                        onMouseUp={handleMouseUp}
                        onMouseLeave={handleMouseUp}
                        style={{ cursor: zoomLevel > 1 ? 'grab' : 'default' }}
                    >
                        <img 
                            src={currentItem?.src} 
                            alt={currentItem?.alt} 
                            className="lightbox-image"
                            style={{
                                transform: `scale(${zoomLevel}) translate(${imagePosition.x / zoomLevel}px, ${imagePosition.y / zoomLevel}px)`,
                                transition: isDragging ? 'none' : 'transform 0.2s ease-out'
                            }}
                            draggable={false}
                        />
                        
                        <div className="lightbox-zoom-controls">
                            <button 
                                onClick={resetZoom}
                                className="lightbox-zoom-btn"
                                aria-label="Wyzeruj powiększenie"
                                title="Wyzeruj powiększenie (0)"
                            >
                                ⟲
                            </button>
                            <button 
                                onClick={zoomOut}
                                className="lightbox-zoom-btn"
                                aria-label="Pomniejsz"
                                title="Pomniejsz (-)"
                            >
                                −
                            </button>
                            <span className="lightbox-zoom-level">
                                {Math.round(zoomLevel * 100)}%
                            </span>
                            <button 
                                onClick={zoomIn}
                                className="lightbox-zoom-btn"
                                aria-label="Powiększ"
                                title="Powiększ (+)"
                            >
                                +
                            </button>
                        </div>

                        <div className="lightbox-counter">
                            {currentIndex + 1} / {items.length}
                        </div>
                    </div>

                    <button 
                        className="lightbox-next" 
                        onClick={nextImage}
                        aria-label="Następne"
                    >
                        ›
                    </button>
                </div>
            )}
        </>
    );
};

export default Lightbox;
